import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Button from '@mui/joy/Button'
import IconButton from '@mui/joy/IconButton'
import Divider from '@mui/joy/Divider'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import VideoCallIcon from '@mui/icons-material/VideoCall'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import GroupsIcon from '@mui/icons-material/Groups'
import ChatIcon from '@mui/icons-material/Chat'

const FEATURES = [
    {
        icon: <RecordVoiceOverIcon sx={{ fontSize: 14 }} />,
        label: 'Meeting transcripts',
        detail: 'Automatically ingest transcripts and recordings from Google Meet meetings',
    },
    {
        icon: <GroupsIcon sx={{ fontSize: 14 }} />,
        label: 'Meeting metadata',
        detail: 'Capture participant lists, duration, meeting titles, and attendance details',
    },
    { icon: <ChatIcon sx={{ fontSize: 14 }} />, label: 'Chat messages', detail: 'Ingest chat messages sent during Google Meet calls' },
]

export function GoogleMeetPanel({ onBack }: { onBack: () => void }) {
    return (
        <Stack gap={2}>
            {/* Header */}
            <Stack direction="row" alignItems="center" gap={1}>
                <IconButton size="sm" variant="plain" color="neutral" onClick={onBack}>
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <VideoCallIcon sx={{ fontSize: 18, color: 'success.500' }} />
                <Stack gap={0} flex={1}>
                    <Typography level="title-sm">Google Meet</Typography>
                    <Typography level="body-xs" color="neutral">
                        Import transcripts, metadata, and chat messages from your Google Meet meetings
                    </Typography>
                </Stack>
                <Chip size="sm" variant="soft" color="neutral" startDecorator={<LinkOffIcon sx={{ fontSize: 10 }} />}>
                    Not connected
                </Chip>
            </Stack>

            {/* Connection card */}
            <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'hidden' }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ px: 1.5, py: 1, bgcolor: 'background.level1', borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <VideoCallIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography level="body-xs" fontWeight="lg" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>
                        Account Connection
                    </Typography>
                </Stack>
                <Stack gap={2} sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <Sheet
                            variant="soft"
                            color="neutral"
                            sx={{ borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        >
                            <LinkOffIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </Sheet>
                        <Stack gap={0.25}>
                            <Typography level="body-sm" fontWeight="md">
                                Not connected
                            </Typography>
                            <Typography level="body-xs" color="neutral">
                                Connect your Google account to enable Google Meet transcript and data ingestion.
                            </Typography>
                        </Stack>
                    </Stack>
                    <Button
                        size="sm"
                        variant="solid"
                        color="success"
                        startDecorator={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        onClick={() => alert('Google Meet OAuth integration is coming soon. Stay tuned!')}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Connect via Google OAuth
                    </Button>
                    <Typography level="body-xs" color="neutral">
                        You'll be redirected to Google to authorize read-only access to your Google Meet meetings, transcripts, and messages. No calls will be
                        made or recordings modified on your behalf.
                    </Typography>
                </Stack>
            </Sheet>

            <Divider />

            {/* What will be synced */}
            <Stack gap={1}>
                <Typography level="body-xs" fontWeight="lg" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10, color: 'text.tertiary' }}>
                    What gets ingested
                </Typography>
                <Stack gap={0.75}>
                    {FEATURES.map((f, i) => (
                        <Sheet key={i} variant="outlined" sx={{ borderRadius: 'sm', px: 1.5, py: 1, opacity: 0.5 }}>
                            <Stack direction="row" alignItems="flex-start" gap={1}>
                                <Stack sx={{ color: 'text.secondary', mt: 0.1 }}>{f.icon}</Stack>
                                <Stack gap={0.15}>
                                    <Typography level="body-xs" fontWeight="md">
                                        {f.label}
                                    </Typography>
                                    <Typography level="body-xs" color="neutral">
                                        {f.detail}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Sheet>
                    ))}
                </Stack>
                <Typography level="body-xs" color="neutral" sx={{ fontSize: 10 }}>
                    Settings to filter by meeting date range, participant, and data type will be available once connected.
                </Typography>
            </Stack>
        </Stack>
    )
}
